// Service Steady PWA — src/components/ReminderControl.tsx — 2026-07-02 (P4)
//
// Per-chat "Remind me" follow-up reminder. Self-contained: renders either a
// "⏰ Remind me" action row (no pending reminder) or a tappable "⏰ Wed
// 2:30 PM" chip (pending — tap to edit/cancel), plus the bottom sheet with
// native date+time inputs (iOS wheels, same choice as the reports range
// picker), PRE-FILLED from a time parsed out of the recent customer
// messages ("I'm off after 2:30pm" → 2:30 PM). The Worker's 1-minute cron
// pushes the owner at due time; the push deep-links back into this
// conversation.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlarmClock, X } from 'lucide-react';
import {
  cancelReminder,
  createReminder,
  listReminders,
  updateReminder,
  type Reminder,
} from '@/lib/api';
import { parseReminderTimeFromMessages, type ParsableMessage } from '@/lib/parseReminderTime';

interface ReminderControlProps {
  slug: string;
  contactId: string;
  conversationId: string;
  /** Contact display for the push copy ("⏰ Reminder: call {label} back"). */
  contactLabel: string;
  /** Recent messages, oldest → newest (used to pre-fill the time). */
  messages: ReadonlyArray<ParsableMessage>;
}

function remindersKey(slug: string, contactId: string) {
  return ['reminders', slug, contactId] as const;
}

/** Local datetime → the two native input values. */
function toInputValues(d: Date): { date: string; time: string } {
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

function chipLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return time;
  return `${d.toLocaleDateString('en-US', { weekday: 'short' })} ${time}`;
}

export function ReminderControl({
  slug,
  contactId,
  conversationId,
  contactLabel,
  messages,
}: ReminderControlProps) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [dateVal, setDateVal] = useState('');
  const [timeVal, setTimeVal] = useState('');
  const [note, setNote] = useState('');

  const pendingQuery = useQuery({
    queryKey: remindersKey(slug, contactId),
    queryFn: () => listReminders({ slug, contactId }),
    enabled: Boolean(slug && contactId),
    staleTime: 30_000,
  });
  const existing: Reminder | undefined = pendingQuery.data?.reminders[0];

  // Pre-fill: existing reminder's time when editing; otherwise a time parsed
  // from the recent messages; otherwise now + 1 hour. Never blocks.
  const suggested = useMemo(() => {
    const parsed = parseReminderTimeFromMessages(messages);
    return parsed ?? new Date(Date.now() + 60 * 60 * 1000);
  }, [messages]);

  useEffect(() => {
    if (!open) return;
    const seed = existing ? new Date(existing.due_at) : suggested;
    const { date, time } = toInputValues(seed);
    setDateVal(date);
    setTimeVal(time);
    setNote(existing?.note ?? '');
  }, [open, existing, suggested]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const invalidate = () => qc.invalidateQueries({ queryKey: remindersKey(slug, contactId) });

  const save = useMutation({
    mutationFn: async () => {
      const dueLocal = new Date(`${dateVal}T${timeVal}:00`);
      const dueAtIso = dueLocal.toISOString();
      if (existing) {
        return updateReminder({ id: existing.id, dueAtIso, note });
      }
      return createReminder({
        slug,
        contactId,
        conversationId,
        label: contactLabel,
        dueAtIso,
        note,
      });
    },
    onSuccess: () => {
      void invalidate();
      setOpen(false);
    },
  });

  const cancel = useMutation({
    mutationFn: async () => {
      if (existing) await cancelReminder({ id: existing.id });
    },
    onSuccess: () => {
      void invalidate();
      setOpen(false);
    },
  });

  const valid = Boolean(dateVal && timeVal) && !Number.isNaN(Date.parse(`${dateVal}T${timeVal}:00`));

  return (
    <>
      {/* Trigger — 2026-07-02 (ReminderPillInline): rendered as a chip inside
          the CallContextBar row, alongside [Summary] [Transcript · 0:41].
          flex-shrink-0 + text-xs match the sibling chips; the row itself
          handles horizontal scroll on narrow screens. Amber when pending,
          quiet "Remind me" add-affordance when not. */}
      {existing ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Edit follow-up reminder"
          className="ss-press flex flex-shrink-0 items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 active:bg-amber-100 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
        >
          <AlarmClock className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="whitespace-nowrap">{chipLabel(existing.due_at)}</span>
          {existing.note ? (
            <span className="max-w-[120px] truncate font-normal opacity-80">
              · {existing.note}
            </span>
          ) : null}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Set a follow-up reminder"
          className="ss-press flex flex-shrink-0 items-center gap-1.5 rounded-full border border-ink-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 dark:border-[#334155] dark:bg-[#1E293B] dark:text-[#CBD5E1]"
        >
          <AlarmClock className="h-3.5 w-3.5 text-ink-500 dark:text-[#94A3B8]" aria-hidden="true" />
          <span className="whitespace-nowrap">Remind me</span>
        </button>
      )}

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-label="Follow-up reminder"
          onClick={() => setOpen(false)}
        >
          <div
            className="flex w-full max-w-md flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl dark:bg-[#0F172A] sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 justify-center pt-2 sm:hidden" aria-hidden="true">
              <div className="h-1 w-9 rounded-full bg-ink-200 dark:bg-[#334155]" />
            </div>
            <div className="flex shrink-0 items-center justify-between border-b border-ink-100 px-4 py-3 dark:border-[#1F2A37]">
              <div className="text-base font-semibold text-ink-900 dark:text-white">
                {existing ? 'Edit reminder' : 'Remind me to follow up'}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full text-ink-500 hover:bg-ink-50 dark:text-[#94A3B8] dark:hover:bg-[#1E293B]"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div
              className="px-4 py-3"
              style={{ paddingBottom: 'calc(var(--tab-h, 64px) + env(safe-area-inset-bottom, 0px))' }}
            >
              <p className="mb-3 text-xs text-ink-500 dark:text-[#94A3B8]">
                You'll get a push at this time that opens {contactLabel || 'this conversation'}.
              </p>
              <div className="grid grid-cols-2 gap-2.5">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-ink-500 dark:text-[#94A3B8]">
                    Date
                  </span>
                  <input
                    type="date"
                    value={dateVal}
                    onChange={(e) => setDateVal(e.target.value)}
                    className="w-full rounded-xl border border-ink-200 bg-white px-3 py-2 text-[15px] text-ink-900 dark:border-[#334155] dark:bg-[#1E293B] dark:text-[#E2E8F0]"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-ink-500 dark:text-[#94A3B8]">
                    Time
                  </span>
                  <input
                    type="time"
                    value={timeVal}
                    onChange={(e) => setTimeVal(e.target.value)}
                    className="w-full rounded-xl border border-ink-200 bg-white px-3 py-2 text-[15px] text-ink-900 dark:border-[#334155] dark:bg-[#1E293B] dark:text-[#E2E8F0]"
                  />
                </label>
              </div>
              <label className="mt-3 block">
                <span className="mb-1 block text-xs font-semibold text-ink-500 dark:text-[#94A3B8]">
                  Note (optional)
                </span>
                <input
                  type="text"
                  value={note}
                  maxLength={300}
                  placeholder="e.g. answer her booking question"
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full rounded-xl border border-ink-200 bg-white px-3 py-2 text-[15px] text-ink-900 placeholder:text-ink-400 dark:border-[#334155] dark:bg-[#1E293B] dark:text-[#E2E8F0]"
                />
              </label>

              <div className="mt-4 flex gap-2">
                {existing ? (
                  <button
                    type="button"
                    onClick={() => cancel.mutate()}
                    disabled={cancel.isPending}
                    className="ss-press flex-1 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-center text-sm font-semibold text-red-700 disabled:opacity-40 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400"
                  >
                    Cancel reminder
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="ss-press flex-1 rounded-xl border border-ink-200 bg-ink-50 px-3 py-2.5 text-center text-sm font-semibold text-ink-900 dark:border-[#334155] dark:bg-[#0F172A] dark:text-[#E2E8F0]"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => save.mutate()}
                  disabled={!valid || save.isPending}
                  className="ss-press flex-1 rounded-xl px-3 py-2.5 text-center text-sm font-semibold text-white disabled:opacity-40"
                  style={{ background: 'var(--ss-accent, #2563EB)' }}
                >
                  {save.isPending ? 'Saving…' : existing ? 'Update' : 'Set reminder'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
